import { API_CONFIG } from '../config/api.ts';
import { authenticatedApiCall } from './api.ts';

// Types for TipOut API
export interface TipOutLog {
  employeeId: number;
  type: 'server' | 'host' | 'bar';
  eventType: 'daily' | 'event';
  shift: string;
  netSales: number;
  bar: number;
  bus: number;
  expo: number;
  totalTipOut: number;
}

export interface BulkTipOutRequest {
  date?: string;
  logs: TipOutLog[];
}

export interface DateSummary {
  date: string;
}

export interface PaginatedDateSummary {
  data: DateSummary[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface CurrentDateResponse {
  date: string;
  timezone: string;
}

// TipOut API calls
export const tipoutApi = {
  // Save tip-out logs (bulk replace)
  saveTipOutLogs: async (data: BulkTipOutRequest, token: string): Promise<TipOutLog[]> => {
    return authenticatedApiCall<TipOutLog[]>(
      API_CONFIG.ENDPOINTS.TIPOUT.BASE,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  // Get current date logs
  getCurrentDateLogs: async (token: string): Promise<TipOutLog[]> => {
    return authenticatedApiCall<TipOutLog[]>(
      API_CONFIG.ENDPOINTS.TIPOUT.BASE,
      {
        method: 'GET',
      },
      token
    );
  },

  // Get date summary with pagination
  getDateSummary: async (
    token: string,
    params: {
      page?: number;
      limit?: number;
      date?: string;
      startDate?: string;
      endDate?: string;
    } = {}
  ): Promise<PaginatedDateSummary> => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.date) queryParams.append('date', params.date);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const endpoint = `${API_CONFIG.ENDPOINTS.TIPOUT.SUMMARY}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    return authenticatedApiCall<PaginatedDateSummary>(
      endpoint,
      {
        method: 'GET',
      },
      token
    );
  },

  // Get current Massachusetts date
  getCurrentDate: async (token: string): Promise<CurrentDateResponse> => {
    return authenticatedApiCall<CurrentDateResponse>(
      API_CONFIG.ENDPOINTS.TIPOUT.CURRENT_DATE,
      {
        method: 'GET',
      },
      token
    );
  },

  // Get tip-out data for a specific date
  getDateLogs: async (token: string, date: string): Promise<TipOutLog[]> => {
    // Ensure only YYYY-MM-DD is sent
    const onlyDate = (date || '').slice(0, 10);
    const queryParams = new URLSearchParams();
    queryParams.append('date', onlyDate);
    
    const endpoint = `${API_CONFIG.ENDPOINTS.TIPOUT.BASE}?${queryParams.toString()}`;
    
    return authenticatedApiCall<TipOutLog[]>(
      endpoint,
      {
        method: 'GET',
      },
      token
    );
  },
};
